class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.onRebuyAdd = this.onRebuyAdd.bind(this);
  }

  connectedCallback() {
    // Fires after Rebuy commits the added product to the Shopify cart.
    document.addEventListener('rebuy:cart.add', this.onRebuyAdd);
  }

  disconnectedCallback() {
    document.removeEventListener('rebuy:cart.add', this.onRebuyAdd);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, { skipRefresh = false } = {}) {
    return (async (triggeredByArg) => {
      const triggered = triggeredByArg || triggeredBy;
      if (triggered) this.setActiveElement(triggered);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      if (!skipRefresh) {
        try {
          // Refresh contents from server to ensure totals/discounts are up-to-date before opening
          await this.refreshContents();
        } catch (e) {
          // ignore refresh failures and open anyway
        }
      }

      // here the animation doesn't seem to always get triggered. a timeout seems to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    })(null);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove(
      'smart-cart--enabled',
      'rebuy-modal-visible',
      'rebuy-cart-visible'
    );    
  }

  // Product added from a Rebuy recommendation widget — refresh the drawer directly.
  async onRebuyAdd() {
    await this.refreshContents();
    if (!this.classList.contains('active')) this.open(null, { skipRefresh: true });
  }

  async refreshContents() {
    // The cart-drawer snippet is wrapped in `{%- unless template == 'cart' -%}`, so it
    // renders nothing when requested against /cart — always fetch against the store root.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const sections = await (await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`)).json();
    const drawer = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');

    // Swap the whole drawer inner (items, totals, empty state, recommendations).
    const inner = this.querySelector('.drawer__inner');
    const nextInner = drawer.querySelector('.drawer__inner');
    if (inner && nextInner) inner.innerHTML = nextInner.innerHTML;

    // cart-icon-bubble renders the inner contents of #cart-icon-bubble.
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && typeof sections['cart-icon-bubble'] === 'string') {
      bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
    }

    const next = drawer.querySelector('cart-drawer');
    this.classList.toggle('is-empty', next ? next.classList.contains('is-empty') : !drawer.querySelector('.cart-item'));

    if (window.Rebuy && typeof window.Rebuy.init === 'function') window.Rebuy.init();
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);
if (typeof CartItems !== 'undefined') {
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
}
// Enable touchpad (trackpad) horizontal swipe support for Rebuy's Flickity slider
(function () {
  let deltaAccum = 0;
  let cooldown = false;

  document.addEventListener(
    'wheel',
    (e) => {
      // Cheapest check first: ignore vertical-dominant scrolls before any DOM/layout work.
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;

      const rebuyContainer = document.getElementById('rebuy-cart-recommendations');
      if (!rebuyContainer) return;

      // getBoundingClientRect forces layout — only reached for horizontal scrolls.
      const rect = rebuyContainer.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;

      e.preventDefault();
      if (cooldown) return;

      deltaAccum += e.deltaX;
      const dir = deltaAccum > 30 ? 'next' : deltaAccum < -30 ? 'previous' : null;
      if (!dir) return;

      const btn = rebuyContainer.querySelector(`.flickity-button.${dir}`);
      if (btn) btn.click();
      deltaAccum = 0;
      cooldown = true;
      setTimeout(() => {
        cooldown = false;
      }, 500);
    },
    { passive: false }
  );
})();
